
import { useEffect, useState } from 'react';
import { useRef } from 'react';
import Cookies from 'universal-cookie';
import FamilyHistory from './FamilyHistory.component';
import { repliValidator } from '../../../components';
import { Box, CircularProgress, Link } from '@material-ui/core';
import useStyles from './SaveFamilyHistory.style';
import { Link as RouterLink } from "react-router-dom";
import { AppStepperButton } from '../profiler/InputComponents';

const cookies = new Cookies();

const SaveFamilyHistory = ({scrollToTop}) => {

    const classes = useStyles();
    const [sending, setSending] = useState(false);
    const [saveResult, setSaveResult] = useState({});
    const [validationError, setValidationError] = useState(false);

    // Family history properties
    const [fhGeneralDiseases, setFhGeneralDiseases] = useState([]);
    const [fhBehavioral, setFhBehavioral] = useState([]);
    const [fhCardiovascular, setFhCardiovascular] = useState([]);
    const [fhEndocrine, setFhEndocrine] = useState([]);
    const [fhGastrointestinal, setFhGastrointestinal] = useState([]);
    const [fhHematologic, setFhHematologic] = useState([]);
    const [fhImmunologic, setFhImmunologic] = useState([]);
    const [fhNeurologic, setFhNeurologic] = useState([]);
    const [fhOncologic, setFhOncologic] = useState([]);
    const [fhPsychiatric, setFhPsychiatric] = useState([]);
    const [fhRespiratory, setFhRespiratory] = useState([]);
    const [fhReproductive, setFhReproductive] = useState([]);
    const familyHistoryValidator = useRef(repliValidator(
       { validators: {
        other_site_of_oncologic: {
            message: 'Please describe other site of Oncologic',
            rule: (val, params, validator) => {
                if (params[0] === 'Other') return val !== '';
                return true;
            },
            required: true
        },
         }
       }));

    //Load profile
    useEffect(() => {
        const getHealthProfile = async () => {
            try {
                let response = await fetch(`${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_PATIENT_PROFILE}`, {
                    headers: {
                        'X-ACCESS-TOKEN': cookies.get('token')
                    }
                });
                if (!response) return;
                let profile = await response.json();
                if (profile.fhGeneral) setFhGeneralDiseases(profile.fhGeneral);
                if (profile.fhBehavioral) setFhBehavioral(profile.fhBehavioral);
                if (profile.fhCardiovascular) setFhCardiovascular(profile.fhCardiovascular);
                if (profile.fhEndocrine) setFhEndocrine(profile.fhEndocrine);
                if (profile.fhGastrointestinal) setFhGastrointestinal(profile.fhGastrointestinal);
                if (profile.fhHematologic) setFhHematologic(profile.fhHematologic);
                if (profile.fhImmunologic) setFhImmunologic(profile.fhImmunologic);
                if (profile.fhNeurologic) setFhNeurologic(profile.fhNeurologic);
                if (profile.fhOncologic) setFhOncologic(profile.fhOncologic);
                if (profile.fhPsychiatric) setFhPsychiatric(profile.fhPsychiatric);
                if (profile.fhRespiratory) setFhRespiratory(profile.fhRespiratory);
                if (profile.fhReproductive) setFhReproductive(profile.fhReproductive);
            } catch (error) {
                console.log(error);
            } finally {

            };
        };

        getHealthProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    // submit family history
    const handleSubmit = async e => {
        e.preventDefault();
        const isValid = familyHistoryValidator.current.allValid();

        if (!isValid) {
            setValidationError(!isValid);
            familyHistoryValidator.current.showMessages();
            setSaveResult({
                severity: "error",
                message: "Please review your answers"
            });
            window.scrollTo({
                top: 0,
                behavior: "smooth"
            });
            return;
        }
        else {
            setSending(true);
            try {
                let response = await fetch(`${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_PATIENT_SAVEFAMILYHISTORY}`, {
                    method: 'post',
                    body: new URLSearchParams({
                        fhGeneralDiseases: JSON.stringify(fhGeneralDiseases),
                        fhBehavioral: JSON.stringify(fhBehavioral),
                        fhCardiovascular: JSON.stringify(fhCardiovascular),
                        fhEndocrine: JSON.stringify(fhEndocrine),
                        fhGastrointestinal: JSON.stringify(fhGastrointestinal),
                        fhHematologic: JSON.stringify(fhHematologic),
                        fhImmunologic: JSON.stringify(fhImmunologic),
                        fhNeurologic: JSON.stringify(fhNeurologic),
                        fhOncologic: JSON.stringify(fhOncologic),
                        fhPsychiatric: JSON.stringify(fhPsychiatric),
                        fhRespiratory: JSON.stringify(fhRespiratory),
                        fhReproductive: JSON.stringify(fhReproductive),
                    }),
                    headers: {
                        'X-ACCESS-TOKEN': cookies.get('token')
                    }
                });
                if (!response.ok) {
                    setSaveResult({
                        severity: "error",
                        message: response.message || response.errors[0].msg
                    });
                    window.scrollTo({
                        top: 0,
                        behavior: "smooth"
                    });
                    return;
                }
                else {
                    let json = await response.json();
                    setSaveResult({
                        severity: "success",
                        message: "Family history saved successfully"
                    });
                }
            } catch (error) {
                console.log(error);
                setSaveResult({
                    severity: "error",
                    message: "Please see the log"
                });
            } finally {
                setSending(false);
                window.scrollTo({
                    top: 0,
                    behavior: "smooth"
                });
            };
        }

    };

    // Family history Functions
    const handleFhAddItemStateArray = (type, newItemDefault) => async e => {
        if (type === "generalAssessment") {
            handleFhTypeAddItemStateArray(fhGeneralDiseases, setFhGeneralDiseases, newItemDefault);
        }
        else if (type === "behavioral") {
            handleFhTypeAddItemStateArray(fhBehavioral, setFhBehavioral, newItemDefault);
        }
        else if (type === "cardiovascular") {
            handleFhTypeAddItemStateArray(fhCardiovascular, setFhCardiovascular, newItemDefault);
        }
        else if (type === "endocrine") {
            handleFhTypeAddItemStateArray(fhEndocrine, setFhEndocrine, newItemDefault);
        }
        else if (type === "gastrointestinal") {
            handleFhTypeAddItemStateArray(fhGastrointestinal, setFhGastrointestinal, newItemDefault);
        }
        else if (type === "hematologic") {
            handleFhTypeAddItemStateArray(fhHematologic, setFhHematologic, newItemDefault);
        }
        else if (type === "immunologic") {
            handleFhTypeAddItemStateArray(fhImmunologic, setFhImmunologic, newItemDefault);
        }
        else if (type === "neurologic") {
            handleFhTypeAddItemStateArray(fhNeurologic, setFhNeurologic, newItemDefault);
        }
        else if (type === "oncologic") {
            handleFhTypeAddItemStateArray(fhOncologic, setFhOncologic, newItemDefault);
        }
        else if (type === "psychiatric") {
            handleFhTypeAddItemStateArray(fhPsychiatric, setFhPsychiatric, newItemDefault);
        }
        else if (type === "respiratory") {
            handleFhTypeAddItemStateArray(fhRespiratory, setFhRespiratory, newItemDefault);
        }
        else if (type === "reproductive") {
            handleFhTypeAddItemStateArray(fhReproductive, setFhReproductive, newItemDefault);
        }
    };

    const handleFhTypeAddItemStateArray = (array, setter, newItemDefault) => {
        let newArray = [...array, newItemDefault];
        setter(newArray);
    }

    
    const handleStateArrayItemChangeOncologic = (array, setter) => (index, field) => e => {
        let modifiedData = array.map((data, i) => {
            if (i === index) {
                if (field === 'othersiteofOncologic') {
                    const validInput = new RegExp(/[a-zA-Z0-9][\\sa-zA-Z0-9]{0,40}/);
                    if (validInput.test(e.target.value) || e.target.value === '') {
                        data[field] = e.target.value;
                    }
                } else {
                    data[field] = e.target.value;
                }
            }
            return data;
        });
        setter(modifiedData);
    };

    const handleFhRemoveItemStateArray = (type, removeIndex) => e => {
        if (type === "generalAssessment") {
            handleFhTypeRemoveItemStateArray(fhGeneralDiseases, setFhGeneralDiseases, removeIndex);
        }
        else if (type === "behavioral") {
            handleFhTypeRemoveItemStateArray(fhBehavioral, setFhBehavioral, removeIndex);
        }
        else if (type === "cardiovascular") {
            handleFhTypeRemoveItemStateArray(fhCardiovascular, setFhCardiovascular, removeIndex);
        }
        else if (type === "endocrine") {
            handleFhTypeRemoveItemStateArray(fhEndocrine, setFhEndocrine, removeIndex);
        }
        else if (type === "gastrointestinal") {
            handleFhTypeRemoveItemStateArray(fhGastrointestinal, setFhGastrointestinal, removeIndex);
        }
        else if (type === "hematologic") {
            handleFhTypeRemoveItemStateArray(fhHematologic, setFhHematologic, removeIndex);
        }
        else if (type === "immunologic") {
            handleFhTypeRemoveItemStateArray(fhImmunologic, setFhImmunologic, removeIndex);
        }
        else if (type === "neurologic") {
            handleFhTypeRemoveItemStateArray(fhNeurologic, setFhNeurologic, removeIndex);
        }
        else if (type === "oncologic") {
            handleFhTypeRemoveItemStateArray(fhOncologic, setFhOncologic, removeIndex);
        }
        else if (type === "psychiatric") {
            handleFhTypeRemoveItemStateArray(fhPsychiatric, setFhPsychiatric, removeIndex);
        }
        else if (type === "respiratory") {
            handleFhTypeRemoveItemStateArray(fhRespiratory, setFhRespiratory, removeIndex);
        }
        else if (type === "reproductive") {
            handleFhTypeRemoveItemStateArray(fhReproductive, setFhReproductive, removeIndex);
        }
    };

    const handleFhTypeRemoveItemStateArray = (array, setter, index) => {
        let remainingData = [...array];
        remainingData.splice(index, 1);
        setter(remainingData);
    }

    const handleFhStateArrayItemChange = (type, index, field, newValue) => e => {
        if (type === "generalAssessment") {
            handleFhTypeStateArrayItemChange(fhGeneralDiseases, setFhGeneralDiseases, index, field, newValue);
        }
        else if (type === "behavioral") {
            handleFhTypeStateArrayItemChange(fhBehavioral, setFhBehavioral, index, field, newValue);
        }
        else if (type === "cardiovascular") {
            handleFhTypeStateArrayItemChange(fhCardiovascular, setFhCardiovascular, index, field, newValue);
        }
        else if (type === "endocrine") {
            handleFhTypeStateArrayItemChange(fhEndocrine, setFhEndocrine, index, field, newValue);
        }
        else if (type === "gastrointestinal") {
            handleFhTypeStateArrayItemChange(fhGastrointestinal, setFhGastrointestinal, index, field, newValue);
        }
        else if (type === "hematologic") {
            handleFhTypeStateArrayItemChange(fhHematologic, setFhHematologic, index, field, newValue);
        }
        else if (type === "immunologic") {
            handleFhTypeStateArrayItemChange(fhImmunologic, setFhImmunologic, index, field, newValue);
        }
        else if (type === "neurologic") {
            handleFhTypeStateArrayItemChange(fhNeurologic, setFhNeurologic, index, field, newValue);
        }
        else if (type === "oncologic") {
            handleFhTypeStateArrayItemChange(fhOncologic, setFhOncologic, index, field, newValue);
        }
        else if (type === "psychiatric") {
            handleFhTypeStateArrayItemChange(fhPsychiatric, setFhPsychiatric, index, field, newValue);
        }
        else if (type === "respiratory") {
            handleFhTypeStateArrayItemChange(fhRespiratory, setFhRespiratory, index, field, newValue);
        }
        else if (type === "reproductive") {
            handleFhTypeStateArrayItemChange(fhReproductive, setFhReproductive, index, field, newValue);
        }
    };

    const handleFhTypeStateArrayItemChange = (array, setter, index, field, newValue) => {
        let dataNewValue = newValue;
          if(newValue.includes('None')){
          dataNewValue = ['None'];
          }
        let modifiedData = array.map(
            (data, i) => {
                if (i === index) data[field] = dataNewValue;
                return data;
            }
        );
        setter(modifiedData);
    };

    const handleMessageClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSaveResult({});
    };

    return (
        <>
            <Box className={classes.root}>

                {/* Family history */}
                <FamilyHistory
                    saveResult={saveResult}
                    fhGeneralDiseases={fhGeneralDiseases}
                    validator={familyHistoryValidator.current}
                    fhBehavioral={fhBehavioral}
                    fhCardiovascular={fhCardiovascular}
                    fhEndocrine={fhEndocrine}
                    fhGastrointestinal={fhGastrointestinal}
                    fhHematologic={fhHematologic}
                    fhImmunologic={fhImmunologic}
                    fhNeurologic={fhNeurologic}
                    fhOncologic={fhOncologic}
                    fhPsychiatric={fhPsychiatric}
                    fhRespiratory={fhRespiratory}
                    fhReproductive={fhReproductive}
                    onFhAdd={(type) => handleFhAddItemStateArray(
                        type,
                        {
                            disease: "",
                            othersiteofOncologic:'',
                            self: "",
                            heirs: []
                        })}
                    onFhRemove={(type, index) => handleFhRemoveItemStateArray(type, index)}
                    onDataChange={handleStateArrayItemChangeOncologic(fhOncologic, setFhOncologic)}
                    onFhDataChange={(type, index, field, newValue) => handleFhStateArrayItemChange(type, index, field, newValue)}
                    handleMessageClose={handleMessageClose}
                    error={validationError}
                />
                {/* Footer buttons */}
                <Box className={classes.regularFooter}>
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <Box flexGrow={1} pr={1}>
                            <Link component={RouterLink} to="/app/dashboard" underline="none">
                                <AppStepperButton
                                    variant="contained"
                                    color="secondary"
                                    fullWidth
                                    size="large"
                                    onClick={scrollToTop}
                                >
                                    Exit
                                </AppStepperButton >
                            </Link>
                        </Box>
                        <Box flexGrow={1} pr={1}>
                            <AppStepperButton
                                style={{ marginBottom: 10 }}
                                onClick={handleSubmit}
                                variant="contained"
                                color="secondary"
                                fullWidth
                                disabled={sending}
                            >
                                Save
                                {sending && <CircularProgress size={24} className={classes.buttonProgress} />}
                            </AppStepperButton>
                        </Box>
                    </Box>
                </Box>

            </Box>
        </>
    );
};

export default SaveFamilyHistory